import * as React from 'react'
import type { PaymentSubscriptionType } from '../../subscription-types'
import type {
  CustomMessages,
  ErrorState,
  MutablePaymentsDialogState,
  PaymentMethodStatus,
  PaymentsData,
  PromoCodeApplied,
  Sections,
} from './payments-dialog-types'

export const PaymentsDialogFormContext = React.createContext<MutablePaymentsDialogState>(null!)

export function PaymentsDialogFormProvider({
  children,
  initialPaymentsData,
  defaultSubscriptionType,
  isUserBillingDetailsPending,
}: {
  children: React.ReactNode
  initialPaymentsData: PaymentsData
  defaultSubscriptionType: PaymentSubscriptionType
  isUserBillingDetailsPending: boolean
}) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<string | null>(null)
  const [paymentMethodStatus, setPaymentMethodStatus] =
    React.useState<PaymentMethodStatus>('initial')
  const [paymentsData, setPaymentsData] = React.useState<PaymentsData>(initialPaymentsData)
  const [addressValid, setAddressValid] = React.useState(false)
  const [override, setSubscriptionType] = React.useState<PaymentSubscriptionType | null>(null)
  const subscriptionType = override || defaultSubscriptionType
  const [customMessages, setCustomMessages] = React.useState<CustomMessages | null>(null)
  const [error, setErrorState] = React.useState<ErrorState>({
    selection: '',
    address: '',
    payment: '',
    total: '',
  })
  const [isLoading, setIsLoading] = React.useState(false)
  const [billingDetailsSynced, setBillingDetailsSynced] = React.useState(
    !isUserBillingDetailsPending,
  )
  const [entryPoint, setEntryPoint] = React.useState<string | undefined>(undefined)
  const [promoCode, setPromoCode] = React.useState('')
  const [promoCodeApplied, setPromoCodeApplied] = React.useState<PromoCodeApplied>(null)

  const setError = React.useCallback(({ error, section }: { error: string; section: Sections }) => {
    setErrorState(prev => ({ ...prev, [section]: error }))
  }, [])

  const resetError = React.useCallback(() => {
    setErrorState({ selection: '', address: '', payment: '', total: '' })
  }, [])

  React.useEffect(() => {
    if (isUserBillingDetailsPending) {
      return
    }

    setPaymentsData(initialPaymentsData)
    setBillingDetailsSynced(true)
  }, [initialPaymentsData, isUserBillingDetailsPending])

  return (
    <PaymentsDialogFormContext.Provider
      value={{
        paymentsData,
        setPaymentsData,
        customMessages,
        setCustomMessages,
        subscriptionType,
        setSubscriptionType,
        error,
        setError,
        resetError,
        addressValid,
        setAddressValid,
        paymentMethodStatus,
        setPaymentMethodStatus,
        isLoading: !billingDetailsSynced || isLoading,
        setIsLoading,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        entryPoint,
        setEntryPoint,
        promoCode,
        setPromoCode,
        promoCodeApplied,
        setPromoCodeApplied,
      }}
    >
      {children}
    </PaymentsDialogFormContext.Provider>
  )
}

export function usePaymentsDialogFormContext() {
  return React.useContext(PaymentsDialogFormContext)
}
